import { enableProdMode, CSP_NONCE } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { Calendar } from 'primeng/calendar';

if (environment.production) {
  enableProdMode();
}

Calendar.prototype.getDateFormat = () => 'dd/mm/yy';

const appRoot = document.querySelector('app-root');
const nonce = appRoot?.getAttribute('ngCspNonce')
  || document.getElementById('csp-nonce')?.getAttribute('content')
  || '';

/**
 * Intercepts ALL inline style writes (el.style.prop = value, el.style.setProperty,
 * including those from Angular animations, Angular Material, and PrimeNG) and
 * redirects them into a single nonce-bearing <style> element using [data-csp-id]
 * attribute selectors. Must run before Angular bootstraps.
 */
function installCspStyleInterceptor(cspNonce: string): void {
  // Patch document.createElement so any <style> element created by third-party
  // libraries (e.g. PrimeNG picklist) automatically receives the CSP nonce,
  // preventing "Applying inline style violates CSP" errors.
  if (cspNonce) {
    const origCreateElement = Document.prototype.createElement;
    (Document.prototype as any).createElement = function(tagName: string, options?: ElementCreationOptions) {
      const el = origCreateElement.call(this, tagName, options);
      if (typeof tagName === 'string' && tagName.toLowerCase() === 'style') {
        (el as HTMLStyleElement).nonce = cspNonce;
      }
      return el;
    };
  }

  const styleEl = document.createElement('style');
  if (cspNonce) styleEl.setAttribute('nonce', cspNonce);
  document.head.appendChild(styleEl);
  const sheet = styleEl.sheet as CSSStyleSheet;

  const tracked = new Map<string, Map<string, string>>();
  const elementTracked = new WeakMap<Element, Map<string, string>>();
  // Cache live CSSStyleRule references to avoid O(n) rule scans on every write
  const ruleCache = new Map<string, CSSStyleRule>();
  // Batch pending flushes into a single microtask to avoid a CSSOM write per property
  const pendingFlush = new Set<string>();
  let flushScheduled = false;
  let counter = 0;

  // WeakRef per element so the periodic sweep can check isConnected without GC dependency.
  const elementRefs = new Map<string, { deref(): Element | undefined }>();

  // Batch-delete a set of orphaned IDs. Uses ruleCache object identity instead of regex
  // so each iteration is O(1) Set.has rather than O(string) regex — critical when thousands
  // of rules accumulate after visiting data-heavy pages like support/trade.
  function deleteOrphanedRules(ids: Set<string>): void {
    if (ids.size === 0) return;
    const targets = new Set<CSSStyleRule>();
    ids.forEach(id => {
      const rule = ruleCache.get(id);
      if (rule) targets.add(rule);
      ruleCache.delete(id);
      tracked.delete(id);
      elementRefs.delete(id);
    });
    if (targets.size === 0) return;
    for (let i = sheet.cssRules.length - 1; i >= 0 && targets.size > 0; i--) {
      const r = sheet.cssRules[i] as CSSStyleRule;
      if (targets.has(r)) {
        sheet.deleteRule(i);
        targets.delete(r);
      }
    }
  }

  function sweepOrphans(): void {
    if (elementRefs.size === 0) return;
    const orphans = new Set<string>();
    elementRefs.forEach((ref, id) => {
      const el = ref.deref();
      if (!el || !el.isConnected) orphans.add(id);
    });
    deleteOrphanedRules(orphans);
  }

  // Sweep immediately after Angular route changes (hash-based routing).
  // Deferred one tick so Angular has finished destroying the previous view's DOM.
  window.addEventListener('hashchange', () => setTimeout(sweepOrphans, 0), { passive: true });

  // Fallback periodic sweep for PrimeNG overlays and other non-routed removals.
  setInterval(sweepOrphans, 5000);

  // FinalizationRegistry as a backup: catches elements GC'd between sweeps.
  const gcRegistry = new (window as any).FinalizationRegistry((id: string) => {
    deleteOrphanedRules(new Set([id]));
  });

  function ensureId(el: Element): string {
    const h = el as HTMLElement;
    if (!h.dataset['cspId']) {
      h.dataset['cspId'] = `s${counter++}`;
      const m = new Map<string, string>();
      elementRefs.set(h.dataset['cspId'], new (window as any).WeakRef(el));
      gcRegistry.register(el, h.dataset['cspId']);
      tracked.set(h.dataset['cspId'], m);
      elementTracked.set(el, m);
    }
    return h.dataset['cspId']!;
  }

  function flushNow(id: string): void {
    let rule = ruleCache.get(id);
    if (!rule) {
      try {
        sheet.insertRule(`[data-csp-id="${id}"] {}`, sheet.cssRules.length);
        rule = sheet.cssRules[sheet.cssRules.length - 1] as CSSStyleRule;
        ruleCache.set(id, rule);
      } catch { return; }
    }
    const m = tracked.get(id);
    // Update in a single cssText write — one CSSOM operation regardless of property count
    rule.style.cssText = m?.size ? Array.from(m.entries()).map(([k, v]) => `${k}: ${v}`).join('; ') : '';
  }

  // height is flushed synchronously so PrimeNG Scroller reads the correct clientHeight
  // in ngAfterViewInit (it calculates numItemsInViewport once and does not use ResizeObserver).
  // top/left are also sync: PrimeNG's overlay connectors (Dropdown/MultiSelect/etc) set an
  // initial position then synchronously read back getBoundingClientRect() in the same tick to
  // decide whether to flip above the trigger. A deferred flush left them reading the stale
  // (pre-write) position, so the flip logic miscalculated wildly negative offsets.
  // width/max-width/min-width stay async: applied to every <td>/<th> (15 000+ writes/render)
  // which was the actual source of the 3747ms freeze. The Splitter forced reflows from height
  // writes are cheap because the hashchange sweep keeps the rule count low between pages.
  const SYNC_PROPS = new Set(['height', 'top', 'left']);

  // Use the native (pre-zone.js) Promise so our flush microtask is invisible to Angular's
  // zone. Without this, zone.js detects the microtask completing and triggers change
  // detection, which rewrites all style bindings, which schedules another flush — infinite loop.
  const NativePromise: PromiseConstructor =
    (window as any).__zone_symbol__Promise ?? Promise;

  function flush(id: string, prop?: string): void {
    if (prop && SYNC_PROPS.has(prop)) {
      flushNow(id);
      pendingFlush.delete(id);
      return;
    }
    pendingFlush.add(id);
    if (!flushScheduled) {
      flushScheduled = true;
      NativePromise.resolve().then(() => {
        flushScheduled = false;
        pendingFlush.forEach(flushNow);
        pendingFlush.clear();
      });
    }
  }

  const dashCaseCache = new Map<string, string>();
  function toDashCase(prop: string): string {
    let cached = dashCaseCache.get(prop);
    if (!cached) {
      cached = prop.replace(/([A-Z])/g, (m) => `-${m.toLowerCase()}`);
      dashCaseCache.set(prop, cached);
    }
    return cached;
  }

  const cache = new WeakMap<Element, CSSStyleDeclaration>();

  function makeProxy(el: Element, real: CSSStyleDeclaration): CSSStyleDeclaration {
    return new Proxy(real, {
      get(target, p) {
        if (p === 'setProperty') {
          return (prop: string, value: string, priority?: string) => {
            const id = ensureId(el);
            const m = elementTracked.get(el)!;
            const nv = priority === 'important' ? `${value ?? ''} !important` : (value ?? '');
            if (m.get(prop) === nv) return; // skip redundant re-write (see set trap)
            m.set(prop, nv);
            flush(id, prop);
          };
        }
        if (p === 'removeProperty') {
          return (prop: string) => {
            const id = (el as HTMLElement).dataset['cspId'];
            const m = id ? elementTracked.get(el) : undefined;
            // Only flush if the property was actually tracked — a remove of an
            // absent property is a no-op and must not trigger a recalc.
            if (m?.has(prop)) { m.delete(prop); flush(id!, prop); }
            return '';
          };
        }
        if (p === 'cssText') {
          const m = elementTracked.get(el);
          if (m?.size) return Array.from(m.entries()).map(([k, v]) => `${k}: ${v}`).join('; ') + ';';
          return '';
        }
        // Return our tracked value so reads are consistent with what was set
        if (typeof p === 'string' && p !== 'length') {
          const m = elementTracked.get(el);
          if (m) {
            const dash = toDashCase(p);
            if (m.has(p)) return m.get(p);
            if (m.has(dash)) return m.get(dash);
          }
        }
        const val = Reflect.get(target, p, target);
        return typeof val === 'function' ? val.bind(target) : val;
      },
      set(target, p, value) {
        if (typeof p === 'string' && p === 'cssText') {
          redirectStyleString(el, String(value ?? ''), false);
          return true;
        }
        if (typeof p === 'string') {
          const id = ensureId(el);
          const dashProp = toDashCase(p);
          const nv = value == null ? '' : String(value);
          const m = elementTracked.get(el)!;
          // Skip no-op writes: PrimeNG overlay/animation code re-assigns the same
          // top/left/height (and grid re-applies identical widths) every frame. Each
          // would otherwise trigger a synchronous CSSOM write + forced style recalc.
          if (m.get(dashProp) === nv) return true;
          m.set(dashProp, nv);
          flush(id, dashProp);
          return true;
        }
        return Reflect.set(target, p, value, target);
      }
    });
  }

  function patchPrototype(proto: typeof HTMLElement.prototype | typeof SVGElement.prototype): void {
    const desc = Object.getOwnPropertyDescriptor(proto, 'style');
    if (!desc?.get) return;
    const origGet = desc.get;
    Object.defineProperty(proto, 'style', {
      get() {
        if (!cache.has(this)) cache.set(this, makeProxy(this, origGet.call(this)));
        return cache.get(this)!;
      },
      configurable: true,
    });
  }

  patchPrototype(HTMLElement.prototype);
  if (Object.getOwnPropertyDescriptor(SVGElement.prototype, 'style')?.get) {
    patchPrototype(SVGElement.prototype as any);
  }

  // Intercept setAttribute/removeAttribute for 'style' which bypass the style proxy
  const origSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function(name: string, value: string) {
    if (name === 'style') {
      redirectStyleString(this, String(value ?? ''), false);
      return;
    }
    origSetAttribute.call(this, name, value);
  };

  const origRemoveAttribute = Element.prototype.removeAttribute;
  Element.prototype.removeAttribute = function(name: string) {
    if (name === 'style') {
      const id = (this as HTMLElement).dataset?.['cspId'];
      if (id) { tracked.get(id)?.clear(); flush(id); }
      return;
    }
    origRemoveAttribute.call(this, name);
  };

  // Parse a CSS declaration string and redirect each property into our tracked map
  function redirectStyleString(el: Element, css: string, merge: boolean): void {
    const id = ensureId(el);
    const map = elementTracked.get(el)!;
    // Build into a fresh map first so we can compare against the current state and skip
    // the flush when Angular re-applies an identical style string (common on re-render).
    const next = merge ? new Map(map) : new Map<string, string>();
    css.split(';').forEach(decl => {
      const colon = decl.indexOf(':');
      if (colon === -1) return;
      const prop = decl.slice(0, colon).trim();
      const val = decl.slice(colon + 1).trim();
      if (prop) next.set(prop, val);
    });
    if (mapsEqual(map, next)) return;
    elementTracked.set(el, next);
    tracked.set(id, next);
    flush(id);
  }

  function mapsEqual(a: Map<string, string>, b: Map<string, string>): boolean {
    if (a.size !== b.size) return false;
    for (const [k, v] of a) { if (b.get(k) !== v) return false; }
    return true;
  }
}

installCspStyleInterceptor(nonce);

platformBrowserDynamic([
  { provide: CSP_NONCE, useValue: nonce }
])
  .bootstrapModule(AppModule)
  .catch(err => console.error(err));
